import './style.css';
import { attachStageLayout } from './ui/stageLayout';
import { createClockView } from './ui/clockView';
import { createStripView } from './ui/stripView';
import { createControlsView } from './ui/controlsView';
import { attachCaptureMode } from './ui/captureMode';
import { getFrameRate, type FrameRateId } from './frame/frameRates';
import { loadPreferences, savePreferences, type Preferences, type ThemeId } from './state/preferences';
import { now as readNow, clearOffset } from './time/timeSource';
import { syncTime, NtpSyncError } from './time/ntpSync';
import { registerSw } from './pwa/registerSw';

const stage = document.getElementById('stage');
const portraitOverlay = document.getElementById('portrait-overlay');
if (!stage || !portraitOverlay) {
  throw new Error('Required DOM elements not found');
}

const clockHost = document.createElement('div');
const stripHost = document.createElement('div');
const controlsHost = document.createElement('div');
stage.appendChild(clockHost);
stage.appendChild(stripHost);
stage.appendChild(controlsHost);

const clockView = createClockView(clockHost);
const stripView = createStripView(stripHost);

let prefs: Preferences = loadPreferences();
document.documentElement.dataset.theme = prefs.theme;

const controlsView = createControlsView(controlsHost, {
  onRateChange(id: FrameRateId) {
    prefs = { ...prefs, rateId: id };
    savePreferences(prefs);
    controlsView.setRate(id);
  },
  onThemeChange(theme: ThemeId) {
    prefs = { ...prefs, theme };
    savePreferences(prefs);
    document.documentElement.dataset.theme = theme;
    controlsView.setTheme(theme);
  },
  onNtpToggle(enabled: boolean) {
    prefs = { ...prefs, ntpSyncEnabled: enabled };
    savePreferences(prefs);
    if (!enabled) {
      clearOffset();
      controlsView.setNtpSync(false);
      return;
    }
    controlsView.setNtpSync(true);
    syncTime().catch((err: unknown) => {
      const message = err instanceof NtpSyncError ? err.message : 'time sync failed';
      controlsView.setNtpSync(false, message);
      prefs = { ...prefs, ntpSyncEnabled: false };
      savePreferences(prefs);
    });
  },
  onCaptureMode() {
    void capture.enterFullscreen();
  },
});

controlsView.setRate(prefs.rateId);
controlsView.setTheme(prefs.theme);
controlsView.setNtpSync(prefs.ntpSyncEnabled);

const capture = attachCaptureMode(document.body, controlsHost);
attachStageLayout(stage, portraitOverlay);

const portraitDismiss = portraitOverlay.querySelector<HTMLButtonElement>('[data-role="portrait-dismiss"]');
portraitDismiss?.addEventListener('click', () => {
  portraitOverlay.dataset.visible = 'false';
});

function tick(): void {
  const rate = getFrameRate(prefs.rateId);
  const nowMs = readNow();
  clockView.update(nowMs, rate);
  stripView.update(nowMs, rate);
  requestAnimationFrame(tick);
}
requestAnimationFrame(tick);

if (prefs.ntpSyncEnabled) {
  syncTime().catch((err: unknown) => {
    const message = err instanceof NtpSyncError ? err.message : 'time sync failed';
    controlsView.setNtpSync(false, message);
    prefs = { ...prefs, ntpSyncEnabled: false };
    savePreferences(prefs);
  });
}

registerSw({
  onUpdateAvailable(activate) {
    const banner = document.createElement('button');
    banner.type = 'button';
    banner.className = 'controls__btn';
    banner.style.position = 'absolute';
    banner.style.bottom = '24px';
    banner.style.right = '24px';
    banner.textContent = 'New version · refresh';
    banner.addEventListener('click', () => activate());
    document.body.appendChild(banner);
  },
});
